/**
 * Central registry for role-aware interview roadmap curation.
 * CommonJS-compatible for Gatsby data imports and Node validation scripts.
 */

const ROLES = [
  { id: "software-engineer", label: "Software Engineer (generalist)" },
  { id: "distributed-systems-engineer", label: "Distributed Systems Engineer" },
  { id: "data-engineer", label: "Data Engineer" },
  {
    id: "ai-engineer",
    label: "AI Engineer",
  },
  { id: "platform-engineer", label: "Infrastructure (Platform) Engineer" },
]

const AREAS = [
  { id: "system-design-use-cases", title: "System Design Use Cases" },
  { id: "dsa", title: "DSA Interview Roadmap" },
  { id: "concurrency", title: "Concurrency & Multithreading" },
  { id: "practical-coding", title: "Practical Coding Rounds" },
  { id: "sql", title: "SQL Interview Roadmap" },
  { id: "networking", title: "Networking for Interviews" },
  { id: "communication", title: "Communication & Data Transfer" },
  { id: "microservices", title: "Microservices Patterns" },
  { id: "security", title: "Security for Engineers" },
  { id: "os", title: "Operating Systems Reference" },
  { id: "lld", title: "OOD & Low-Level Design" },
  { id: "distributed", title: "Distributed Systems" },
  { id: "databases", title: "Databases Reference" },
  { id: "data-engineering", title: "Data Engineering Reference" },
  { id: "observability", title: "Observability Reference" },
  { id: "ai-engineering", title: "AI Engineering Reference" },
  { id: "ai-systems", title: "AI Systems Reference" },
]

const IMPORTANCE_LEVELS = {
  1: { symbol: "!!!", label: "Critical" },
  2: { symbol: "!!", label: "Recommended" },
  3: { symbol: "!", label: "Nice to have" },
  4: { symbol: "~", label: "Optional" },
}

const SYSTEM_DESIGN_ALL = [
  "methodology",
  "building-blocks",
  "storage-and-files",
  "feeds-and-social",
  "messaging-realtime",
  "media-streaming",
  "search-discovery",
  "location-marketplaces",
  "payments-fintech",
  "platform-observability",
  "staff-deep-dives",
]

const SYSTEM_DESIGN_DSE_PLATFORM = [
  "methodology",
  "building-blocks",
  "storage-and-files",
  "messaging-realtime",
  "media-streaming",
  "search-discovery",
  "platform-observability",
  "staff-deep-dives",
]

const SYSTEM_DESIGN_DE = [
  "methodology",
  "building-blocks",
  "storage-and-files",
  "media-streaming",
  "search-discovery",
  "platform-observability",
  "staff-deep-dives",
]

const SYSTEM_DESIGN_AI = [
  "methodology",
  "building-blocks",
  "storage-and-files",
  "feeds-and-social",
  "messaging-realtime",
  "media-streaming",
  "search-discovery",
  "platform-observability",
  "staff-deep-dives",
]

const DSA_CORE = [
  "foundations",
  "arrays-hashing",
  "two-pointers",
  "sliding-window",
  "stack",
  "binary-search",
  "sorting",
  "linked-list",
  "design-problems",
  "trees",
  "tries",
  "heaps",
  "backtracking",
  "graphs",
  "dynamic-programming",
  "greedy",
  "intervals",
  "bit-manipulation",
  "math-geometry",
  "advanced-strings",
]

const DSA_DATA_ENGINEER = [
  "foundations",
  "arrays-hashing",
  "two-pointers",
  "sliding-window",
  "stack",
  "binary-search",
  "sorting",
  "linked-list",
  "design-problems",
  "trees",
  "heaps",
  "graphs",
  "dynamic-programming",
  "intervals",
]

const CONCURRENCY_SWE = [
  "concurrency-foundations",
  "race-conditions-critical-sections",
  "locks-mutual-exclusion",
  "semaphores-classic-sync-problems",
  "condition-variables-monitors",
  "deadlock-livelock-starvation",
  "memory-models-atomics",
  "coordination-primitives",
  "thread-pools-executors",
  "async-event-driven-concurrency",
  "classic-concurrency-interview-patterns",
]

const CONCURRENCY_DSE_PLATFORM = [
  "concurrency-foundations",
  "race-conditions-critical-sections",
  "locks-mutual-exclusion",
  "semaphores-classic-sync-problems",
  "condition-variables-monitors",
  "deadlock-livelock-starvation",
  "memory-models-atomics",
  "coordination-primitives",
  "thread-pools-executors",
  "async-event-driven-concurrency",
  "parallel-algorithm-patterns",
  "classic-concurrency-interview-patterns",
]

const CONCURRENCY_DE_AI = [
  "concurrency-foundations",
  "race-conditions-critical-sections",
  "locks-mutual-exclusion",
  "semaphores-classic-sync-problems",
  "deadlock-livelock-starvation",
  "memory-models-atomics",
  "thread-pools-executors",
  "async-event-driven-concurrency",
  "parallel-algorithm-patterns",
]

const PRACTICAL_FULL = [
  "practical-round-foundations",
  "debugging-unfamiliar-code",
  "extending-an-existing-codebase",
  "testing-under-time-pressure",
  "refactoring-code-review-rounds",
  "api-data-integration-exercises",
  "machine-coding-execution-speed",
  "collaborative-ai-assisted-coding",
]

const PRACTICAL_NO_MACHINE_CODING = [
  "practical-round-foundations",
  "debugging-unfamiliar-code",
  "extending-an-existing-codebase",
  "testing-under-time-pressure",
  "refactoring-code-review-rounds",
  "api-data-integration-exercises",
  "collaborative-ai-assisted-coding",
]

const SQL_FULL = [
  "sql-query-fundamentals",
  "joins-multi-table-queries",
  "aggregation-grouping",
  "subqueries-ctes",
  "window-functions",
  "sql-interview-theory-essentials",
]

const SQL_OPERATIONAL = [
  "sql-query-fundamentals",
  "joins-multi-table-queries",
  "aggregation-grouping",
  "sql-interview-theory-essentials",
]

const NETWORKING_FULL = [
  "networking-foundations",
  "transport-layer",
  "dns",
  "tls-https",
  "http-evolution",
  "proxies-load-balancing-cdn",
  "debugging-basics",
]

const NETWORKING_DE = [
  "networking-foundations",
  "transport-layer",
  "dns",
  "tls-https",
  "proxies-load-balancing-cdn",
  "debugging-basics",
]

const COMMUNICATION_SWE_AI = [
  "comm-foundations",
  "rest-http-apis",
  "json-api-contracts",
  "graphql",
  "grpc-modern-rpc",
  "realtime-protocols",
  "async-integration",
  "bulk-large-transfer",
]

const COMMUNICATION_DSE_PLATFORM = [
  "comm-foundations",
  "rest-http-apis",
  "json-api-contracts",
  "grpc-modern-rpc",
  "realtime-protocols",
  "async-integration",
  "bulk-large-transfer",
  "deep-cuts",
]

const COMMUNICATION_DE = [
  "comm-foundations",
  "rest-http-apis",
  "json-api-contracts",
  "grpc-modern-rpc",
  "async-integration",
  "bulk-large-transfer",
]

const MICROSERVICES_SWE_AI = [
  "ms-foundations",
  "service-discovery",
  "api-gateway-bff",
  "service-mesh",
  "event-sourcing-cqrs",
  "platform-resilience-deployment",
]

const MICROSERVICES_DSE_PLATFORM = [
  "ms-foundations",
  "service-discovery",
  "api-gateway-bff",
  "service-mesh",
  "event-sourcing-cqrs",
  "platform-resilience-deployment",
  "deep-cuts",
]

const MICROSERVICES_DE = [
  "ms-foundations",
  "service-discovery",
  "platform-resilience-deployment",
]

const SECURITY_SWE = [
  "sec-foundations",
  "authn-authz",
  "web-browser-security",
  "api-service-security",
  "distributed-platform-security",
  "data-privacy-compliance",
  "secure-sdlc-operations",
]

const SECURITY_DSE_PLATFORM = [
  "sec-foundations",
  "authn-authz",
  "api-service-security",
  "distributed-platform-security",
  "data-privacy-compliance",
  "secure-sdlc-operations",
  "deep-cuts",
]

const SECURITY_DE = [
  "sec-foundations",
  "authn-authz",
  "api-service-security",
  "distributed-platform-security",
  "data-privacy-compliance",
  "secure-sdlc-operations",
]

const SECURITY_AI = [
  "sec-foundations",
  "authn-authz",
  "web-browser-security",
  "api-service-security",
  "distributed-platform-security",
  "data-privacy-compliance",
  "secure-sdlc-operations",
  "deep-cuts",
]

const OS_FULL = [
  "os-foundations",
  "cpu-virtualization",
  "memory-virtualization",
  "memory-allocation",
  "persistence-io",
  "containers-virtualization",
]

const OS_DE = [
  "os-foundations",
  "cpu-virtualization",
  "memory-virtualization",
  "persistence-io",
  "containers-virtualization",
]

const LLD_SWE = [
  "ood-foundations",
  "design-patterns-core",
  "lld-interview-process",
  "classic-problems-allocation",
  "classic-problems-infrastructure",
  "api-component-design",
]

const LLD_DSE_AI_PLATFORM = [
  "ood-foundations",
  "design-patterns-core",
  "lld-interview-process",
  "classic-problems-infrastructure",
  "api-component-design",
]

const DISTRIBUTED_CORE = [
  "dist-foundations",
  "consistency-models",
  "replication",
  "partitioning",
  "time-ordering",
  "consensus-coordination",
  "distributed-transactions",
  "messaging-streams",
]

const DISTRIBUTED_DSE_PLATFORM = [
  "dist-foundations",
  "consistency-models",
  "replication",
  "partitioning",
  "time-ordering",
  "consensus-coordination",
  "distributed-transactions",
  "messaging-streams",
  "deep-cuts",
  "optional-coding-practice",
]

const DATABASES_ALL = [
  "relational-modeling",
  "storage-internals",
  "transactions-mvcc",
  "replication-ha",
  "sharding-federation",
  "nosql-families",
  "key-value-specialized",
  "object-lakes-warehouses",
  "search-columnar",
]

const DATA_ENGINEERING_DSE = [
  "de-foundations",
  "distributed-batch-compute",
  "stream-processing",
  "production-pipeline-ops",
  "deep-cuts",
]

const DATA_ENGINEERING_DE = [
  "de-foundations",
  "distributed-batch-compute",
  "apache-spark",
  "stream-processing",
  "workflow-orchestration",
  "elt-lakehouse-pipelines",
  "data-quality-governance",
  "production-pipeline-ops",
  "deep-cuts",
]

const DATA_ENGINEERING_AI = [
  "de-foundations",
  "distributed-batch-compute",
  "apache-spark",
  "stream-processing",
  "workflow-orchestration",
  "data-quality-governance",
  "production-pipeline-ops",
]

const DATA_ENGINEERING_PLATFORM = [
  "de-foundations",
  "distributed-batch-compute",
  "workflow-orchestration",
  "production-pipeline-ops",
  "deep-cuts",
]

const OBSERVABILITY_ALL = [
  "obs-foundations",
  "logging",
  "metrics",
  "distributed-tracing",
  "slos-error-budgets",
  "alerting-incidents",
  "observability-platforms",
  "production-debugging",
]

const AI_ENGINEERING_SWE_DE = [
  "llm-foundations",
  "prompting-context",
  "ai-assisted-development",
  "rag-knowledge-bases",
  "mcp-tools-automation",
  "models-selection-tuning",
  "team-practices-governance",
]

const AI_ENGINEERING_DSE = [
  "llm-foundations",
  "ai-assisted-development",
  "mcp-tools-automation",
  "models-selection-tuning",
  "team-practices-governance",
]

const AI_ENGINEERING_AI = [
  "llm-foundations",
  "prompting-context",
  "ai-assisted-development",
  "rag-knowledge-bases",
  "mcp-tools-automation",
  "multi-agent-systems",
  "models-selection-tuning",
  "team-practices-governance",
]

const AI_ENGINEERING_PLATFORM = [
  "llm-foundations",
  "prompting-context",
  "ai-assisted-development",
  "mcp-tools-automation",
  "models-selection-tuning",
  "team-practices-governance",
]

const AI_SYSTEMS_SWE = [
  "ai-systems-foundations",
  "architecture-patterns",
  "reliability-safety",
  "evaluation-quality",
  "cost-capacity",
  "shipping-operations",
]

const AI_SYSTEMS_DE = [
  "ai-systems-foundations",
  "architecture-patterns",
  "knowledge-pipelines",
  "evaluation-quality",
  "shipping-operations",
]

const AI_SYSTEMS_AI = [
  "ai-systems-foundations",
  "architecture-patterns",
  "model-serving",
  "knowledge-pipelines",
  "reliability-safety",
  "evaluation-quality",
  "cost-capacity",
  "shipping-operations",
  "deep-cuts",
]

const AI_SYSTEMS_PLATFORM = [
  "ai-systems-foundations",
  "architecture-patterns",
  "model-serving",
  "reliability-safety",
  "evaluation-quality",
  "cost-capacity",
  "shipping-operations",
]

const ROLE_AREA_CONFIG = {
  "software-engineer": {
    "system-design-use-cases": {
      importance: 1,
      reason: "Decisive Senior+ HLD round across product domains",
      selectedTopicIds: SYSTEM_DESIGN_ALL,
    },
    dsa: {
      importance: 1,
      reason: "Standard FAANG coding gate with broad pattern coverage",
      selectedTopicIds: DSA_CORE,
    },
    concurrency: {
      importance: 2,
      reason: "Backend loops probe shared-state and coordination correctness",
      selectedTopicIds: CONCURRENCY_SWE,
    },
    "practical-coding": {
      importance: 1,
      reason: "Debugging and repo-extension formats are increasingly common",
      selectedTopicIds: PRACTICAL_FULL,
    },
    sql: {
      importance: 2,
      reason: "Frequent backend probe for joins, aggregation, and analytics",
      selectedTopicIds: SQL_FULL,
    },
    networking: {
      importance: 2,
      reason: "HLD latency budgets and connection-level debugging depth",
      selectedTopicIds: NETWORKING_FULL,
    },
    communication: {
      importance: 1,
      reason: "API contracts and integration style are heavily tested",
      selectedTopicIds: COMMUNICATION_SWE_AI,
    },
    microservices: {
      importance: 2,
      reason: "Production-design vocabulary for scoped service boundaries",
      selectedTopicIds: MICROSERVICES_SWE_AI,
    },
    security: {
      importance: 2,
      reason: "OAuth, OWASP, and secrets follow-ups in design rounds",
      selectedTopicIds: SECURITY_SWE,
    },
    os: {
      importance: 2,
      reason: "Memory, scheduling, and container trade-offs in depth",
      selectedTopicIds: OS_FULL,
    },
    lld: {
      importance: 1,
      reason: "Dedicated OOD round still common at Senior+",
      selectedTopicIds: LLD_SWE,
    },
    distributed: {
      importance: 1,
      reason: "Core Senior+ HLD correctness and failure reasoning",
      selectedTopicIds: DISTRIBUTED_CORE,
    },
    databases: {
      importance: 1,
      reason: "Storage choice and replication trade-offs are central",
      selectedTopicIds: DATABASES_ALL,
    },
    "data-engineering": {
      importance: 4,
      reason: "Pipeline specialization outside typical SWE loops",
      selectedTopicIds: [],
    },
    observability: {
      importance: 2,
      reason: "Production-readiness signal in design follow-ups",
      selectedTopicIds: OBSERVABILITY_ALL,
    },
    "ai-engineering": {
      importance: 2,
      reason: "Mid-2026 baseline for LLM-assisted engineering workflows",
      selectedTopicIds: AI_ENGINEERING_SWE_DE,
    },
    "ai-systems": {
      importance: 3,
      reason: "Relevant when the product ships LLM-powered features",
      selectedTopicIds: AI_SYSTEMS_SWE,
    },
  },
  "distributed-systems-engineer": {
    "system-design-use-cases": {
      importance: 1,
      reason: "Infra and correctness assembly is the defining HLD round",
      selectedTopicIds: SYSTEM_DESIGN_DSE_PLATFORM,
    },
    dsa: {
      importance: 1,
      reason: "FAANG coding gate still applies despite systems focus",
      selectedTopicIds: DSA_CORE,
    },
    concurrency: {
      importance: 1,
      reason: "Core correctness substrate for distributed primitives",
      selectedTopicIds: CONCURRENCY_DSE_PLATFORM,
    },
    "practical-coding": {
      importance: 2,
      reason: "Debugging unfamiliar codebases and repo formats matter",
      selectedTopicIds: PRACTICAL_NO_MACHINE_CODING,
    },
    sql: {
      importance: 3,
      reason: "Basic operational fluency is sufficient for most loops",
      selectedTopicIds: SQL_OPERATIONAL,
    },
    networking: {
      importance: 1,
      reason: "Fundamental to failure reasoning and protocol depth",
      selectedTopicIds: NETWORKING_FULL,
    },
    communication: {
      importance: 2,
      reason: "RPC, backpressure, and async boundaries in service design",
      selectedTopicIds: COMMUNICATION_DSE_PLATFORM,
    },
    microservices: {
      importance: 2,
      reason: "Operational distributed-system layer above core primitives",
      selectedTopicIds: MICROSERVICES_DSE_PLATFORM,
    },
    security: {
      importance: 2,
      reason: "Trust boundaries and secrets affect system correctness",
      selectedTopicIds: SECURITY_DSE_PLATFORM,
    },
    os: {
      importance: 1,
      reason: "Core systems substrate for scheduling and memory behavior",
      selectedTopicIds: OS_FULL,
    },
    lld: {
      importance: 3,
      reason: "Secondary to protocol and large-scale system design",
      selectedTopicIds: LLD_DSE_AI_PLATFORM,
    },
    distributed: {
      importance: 1,
      reason: "Defining domain — consistency, consensus, and messaging",
      selectedTopicIds: DISTRIBUTED_DSE_PLATFORM,
    },
    databases: {
      importance: 1,
      reason: "Storage internals and replication depth are central",
      selectedTopicIds: DATABASES_ALL,
    },
    "data-engineering": {
      importance: 3,
      reason: "Useful context for streaming and data-infra adjacent roles",
      selectedTopicIds: DATA_ENGINEERING_DSE,
    },
    observability: {
      importance: 1,
      reason: "Required for distributed diagnosis and incident response",
      selectedTopicIds: OBSERVABILITY_ALL,
    },
    "ai-engineering": {
      importance: 3,
      reason: "Useful tooling literacy, not a core loop focus",
      selectedTopicIds: AI_ENGINEERING_DSE,
    },
    "ai-systems": {
      importance: 4,
      reason: "Only relevant for AI-infra specialization tracks",
      selectedTopicIds: [],
    },
  },
  "data-engineer": {
    "system-design-use-cases": {
      importance: 1,
      reason: "Data-platform and pipeline architecture is a core HLD round",
      selectedTopicIds: SYSTEM_DESIGN_DE,
    },
    dsa: {
      importance: 2,
      reason: "Common but usually lighter than SWE coding depth",
      selectedTopicIds: DSA_DATA_ENGINEER,
    },
    concurrency: {
      importance: 3,
      reason: "Engine literacy for Spark/Flink, rarely a dedicated round",
      selectedTopicIds: CONCURRENCY_DE_AI,
    },
    "practical-coding": {
      importance: 2,
      reason: "Pipeline integration and repository-extension formats appear",
      selectedTopicIds: PRACTICAL_NO_MACHINE_CODING,
    },
    sql: {
      importance: 1,
      reason: "Dedicated high-weight round for query and modeling depth",
      selectedTopicIds: SQL_FULL,
    },
    networking: {
      importance: 3,
      reason: "Useful platform substrate for data movement and security",
      selectedTopicIds: NETWORKING_DE,
    },
    communication: {
      importance: 2,
      reason: "Contracts and bulk data movement between pipeline services",
      selectedTopicIds: COMMUNICATION_DE,
    },
    microservices: {
      importance: 3,
      reason: "Relevant mainly when data platforms expose service APIs",
      selectedTopicIds: MICROSERVICES_DE,
    },
    security: {
      importance: 2,
      reason: "PII handling, governance, and pipeline isolation matter",
      selectedTopicIds: SECURITY_DE,
    },
    os: {
      importance: 3,
      reason: "Useful for Spark runtime and I/O diagnosis",
      selectedTopicIds: OS_DE,
    },
    lld: {
      importance: 4,
      reason: "Rarely a dedicated DE round at Senior+",
      selectedTopicIds: [],
    },
    distributed: {
      importance: 1,
      reason: "Pipeline correctness depends on distributed semantics",
      selectedTopicIds: DISTRIBUTED_CORE,
    },
    databases: {
      importance: 1,
      reason: "Core storage, warehouse, and lake knowledge",
      selectedTopicIds: DATABASES_ALL,
    },
    "data-engineering": {
      importance: 1,
      reason: "Defining domain — batch, streaming, and lakehouse pipelines",
      selectedTopicIds: DATA_ENGINEERING_DE,
    },
    observability: {
      importance: 1,
      reason: "Pipeline reliability, SLAs, and data-quality monitoring",
      selectedTopicIds: OBSERVABILITY_ALL,
    },
    "ai-engineering": {
      importance: 3,
      reason: "Useful on AI-adjacent data teams building RAG corpora",
      selectedTopicIds: AI_ENGINEERING_SWE_DE,
    },
    "ai-systems": {
      importance: 3,
      reason: "Relevant for ML-heavy pipelines and knowledge ingestion",
      selectedTopicIds: AI_SYSTEMS_DE,
    },
  },
  "ai-engineer": {
    "system-design-use-cases": {
      importance: 1,
      reason: "LLM, RAG, and agent architecture is the central HLD round",
      selectedTopicIds: SYSTEM_DESIGN_AI,
    },
    dsa: {
      importance: 1,
      reason: "SWE coding bar still applies to applied AI product teams",
      selectedTopicIds: DSA_CORE,
    },
    concurrency: {
      importance: 2,
      reason: "Serving throughput and pipeline parallelism in production",
      selectedTopicIds: CONCURRENCY_DE_AI,
    },
    "practical-coding": {
      importance: 1,
      reason: "AI-assisted practical work is increasingly likely",
      selectedTopicIds: PRACTICAL_FULL,
    },
    sql: {
      importance: 2,
      reason: "Data access, metadata, and analytics queries remain common",
      selectedTopicIds: SQL_FULL,
    },
    networking: {
      importance: 2,
      reason: "Streaming APIs and model-serving latency depend on it",
      selectedTopicIds: NETWORKING_FULL,
    },
    communication: {
      importance: 1,
      reason: "Streaming, tool APIs, and model integration contracts",
      selectedTopicIds: COMMUNICATION_SWE_AI,
    },
    microservices: {
      importance: 2,
      reason: "AI services use the same deployment and gateway patterns",
      selectedTopicIds: MICROSERVICES_SWE_AI,
    },
    security: {
      importance: 1,
      reason: "Prompt injection, tool access, and data risks are central",
      selectedTopicIds: SECURITY_AI,
    },
    os: {
      importance: 2,
      reason: "Serving memory pressure, containers, and GPU host behavior",
      selectedTopicIds: OS_FULL,
    },
    lld: {
      importance: 2,
      reason: "Service and component design for agents and tool routers",
      selectedTopicIds: LLD_DSE_AI_PLATFORM,
    },
    distributed: {
      importance: 1,
      reason: "Production AI is inherently distributed and failure-prone",
      selectedTopicIds: DISTRIBUTED_CORE,
    },
    databases: {
      importance: 1,
      reason: "Metadata stores, vectors, state, and retrieval backends",
      selectedTopicIds: DATABASES_ALL,
    },
    "data-engineering": {
      importance: 2,
      reason: "Training and RAG corpora need reliable ingestion pipelines",
      selectedTopicIds: DATA_ENGINEERING_AI,
    },
    observability: {
      importance: 1,
      reason: "Quality, cost, latency, and drift monitoring for LLM products",
      selectedTopicIds: OBSERVABILITY_ALL,
    },
    "ai-engineering": {
      importance: 1,
      reason: "Defining domain — prompting, RAG, agents, and governance",
      selectedTopicIds: AI_ENGINEERING_AI,
    },
    "ai-systems": {
      importance: 1,
      reason: "Defining production domain — serving, evals, and rollouts",
      selectedTopicIds: AI_SYSTEMS_AI,
    },
  },
  "platform-engineer": {
    "system-design-use-cases": {
      importance: 1,
      reason: "Capacity, reliability, and multi-tenant platform design",
      selectedTopicIds: SYSTEM_DESIGN_DSE_PLATFORM,
    },
    dsa: {
      importance: 1,
      reason: "Standard coding gate for platform and SRE-adjacent loops",
      selectedTopicIds: DSA_CORE,
    },
    concurrency: {
      importance: 1,
      reason: "Runtime and resource control are core platform concerns",
      selectedTopicIds: CONCURRENCY_DSE_PLATFORM,
    },
    "practical-coding": {
      importance: 1,
      reason: "Troubleshooting and repo work are daily platform skills",
      selectedTopicIds: PRACTICAL_FULL,
    },
    sql: {
      importance: 3,
      reason: "Operational fluency for datastore incidents, not specialization",
      selectedTopicIds: SQL_OPERATIONAL,
    },
    networking: {
      importance: 1,
      reason: "Core platform substrate for connectivity and load paths",
      selectedTopicIds: NETWORKING_FULL,
    },
    communication: {
      importance: 2,
      reason: "Control-plane and data-plane service interfaces",
      selectedTopicIds: COMMUNICATION_DSE_PLATFORM,
    },
    microservices: {
      importance: 1,
      reason: "Service platform architecture is the defining scope",
      selectedTopicIds: MICROSERVICES_DSE_PLATFORM,
    },
    security: {
      importance: 1,
      reason: "Identity, supply chain, and tenancy are platform mandates",
      selectedTopicIds: SECURITY_DSE_PLATFORM,
    },
    os: {
      importance: 1,
      reason: "Core platform substrate — kernels, cgroups, and containers",
      selectedTopicIds: OS_FULL,
    },
    lld: {
      importance: 2,
      reason: "Useful for control-plane components and internal APIs",
      selectedTopicIds: LLD_DSE_AI_PLATFORM,
    },
    distributed: {
      importance: 1,
      reason: "Defining platform domain — coordination at fleet scale",
      selectedTopicIds: DISTRIBUTED_DSE_PLATFORM,
    },
    databases: {
      importance: 2,
      reason: "Operate and integrate storage rather than model from scratch",
      selectedTopicIds: DATABASES_ALL,
    },
    "data-engineering": {
      importance: 3,
      reason: "Relevant when the platform owns batch and workflow workloads",
      selectedTopicIds: DATA_ENGINEERING_PLATFORM,
    },
    observability: {
      importance: 1,
      reason: "Defining operational competency for platform teams",
      selectedTopicIds: OBSERVABILITY_ALL,
    },
    "ai-engineering": {
      importance: 3,
      reason: "Automation and internal tooling benefit from LLM workflows",
      selectedTopicIds: AI_ENGINEERING_PLATFORM,
    },
    "ai-systems": {
      importance: 3,
      reason: "Relevant when operating shared inference infrastructure",
      selectedTopicIds: AI_SYSTEMS_PLATFORM,
    },
  },
}

const ROLE_IDS = new Set(ROLES.map(role => role.id))
const AREA_IDS = new Set(AREAS.map(area => area.id))

const PROFILES = Object.fromEntries(
  ROLES.map(role => [
    role.id,
    {
      ...role,
      areas: ROLE_AREA_CONFIG[role.id],
    },
  ]),
)

function isValidRoleId(roleId) {
  return typeof roleId === "string" && ROLE_IDS.has(roleId)
}

function getRoleProfile(roleId) {
  if (!isValidRoleId(roleId)) return null
  return PROFILES[roleId]
}

function getAreaConfig(roleId, areaId) {
  if (!isValidRoleId(roleId) || !AREA_IDS.has(areaId)) return null
  return ROLE_AREA_CONFIG[roleId][areaId] || null
}

function isTopicSelected(roleId, areaId, topicId) {
  const areaConfig = getAreaConfig(roleId, areaId)
  if (!areaConfig || typeof topicId !== "string") return false
  return areaConfig.selectedTopicIds.includes(topicId)
}

function getImportanceLevel(importance) {
  return IMPORTANCE_LEVELS[importance] || null
}

module.exports = {
  ROLES,
  AREAS,
  IMPORTANCE_LEVELS,
  PROFILES,
  ROLE_AREA_CONFIG,
  isValidRoleId,
  getRoleProfile,
  getAreaConfig,
  isTopicSelected,
  getImportanceLevel,
}
